import { useState, useEffect } from 'react';
import {
  FormControl,
  FormLabel,
  VStack,
  RadioGroup,
  Radio,
  CheckboxGroup,
  Checkbox,
  Button,
  FormHelperText,
  FormErrorMessage,
  Text,
  Spinner,
  Box,
} from '@chakra-ui/react';
import { useActivityResponse } from '../../hooks/useActivityResponse';
import type { ActivityComponentProps } from '../../types/learningActivities';

interface MultipleChoiceQuestionProps extends ActivityComponentProps {
  options?: string[];
  allowMultiple?: boolean;
}

export function MultipleChoiceQuestion({
  activityKey,
  required = false,
  options: propOptions,
  allowMultiple: propAllowMultiple,
  onResponseChange,
}: MultipleChoiceQuestionProps) {
  const { activity, response, isLoading, error, saveResponse, isSaving } = useActivityResponse(activityKey);
  const [selectedValue, setSelectedValue] = useState<string>('');
  const [selectedValues, setSelectedValues] = useState<string[]>([]);

  // Initialize from existing response
  useEffect(() => {
    if (response) {
      const data = response.response_data || {};
      if (data.selected_options && Array.isArray(data.selected_options)) {
        setSelectedValues(data.selected_options);
      } else if (response.response_value) {
        setSelectedValue(response.response_value);
      }
    }
  }, [response]);

  if (isLoading) {
    return (
      <Box py={2}>
        <Spinner size="sm" />
      </Box>
    );
  }

  if (error || !activity) {
    return (
      <Text color="red.500" fontSize="sm">
        {error || 'Failed to load activity'}
      </Text>
    );
  }

  const config = activity.config || {};
  const options = propOptions || config.options || [];
  const allowMultiple = propAllowMultiple ?? config.allow_multiple ?? false;

  const handleSingleChange = async (value: string) => {
    setSelectedValue(value);
    try {
      await saveResponse(value);
      onResponseChange?.(value);
    } catch (err) {
      console.error('Failed to save response:', err);
    }
  };

  const handleClearSelection = async () => {
    setSelectedValue('');
    try {
      await saveResponse(null);
      onResponseChange?.(null);
    } catch (err) {
      console.error('Failed to clear response:', err);
    }
  };

  const handleMultipleChange = async (values: string[]) => {
    setSelectedValues(values);
    try {
      await saveResponse(
        values.join(', '), // Store as comma-separated string
        { selected_options: values }
      );
      onResponseChange?.(values.join(', '), { selected_options: values });
    } catch (err) {
      console.error('Failed to save response:', err);
    }
  };

  const hasValue = allowMultiple ? selectedValues.length > 0 : selectedValue !== '';

  return (
    <FormControl isRequired={required} isInvalid={required && !hasValue}>
      <FormLabel fontWeight="semibold">
        {activity.prompt}
        {required && <Text as="span" color="red.500" ml={1}>*</Text>}
      </FormLabel>

      {config.help_text && (
        <FormHelperText mb={3}>{config.help_text}</FormHelperText>
      )}

      {allowMultiple ? (
        <CheckboxGroup value={selectedValues} onChange={handleMultipleChange}>
          <VStack align="start" spacing={2}>
            {options.map((option, index) => (
              <Checkbox key={index} value={option}>
                {option}
              </Checkbox>
            ))}
          </VStack>
        </CheckboxGroup>
      ) : (
        <Box>
          <RadioGroup value={selectedValue} onChange={handleSingleChange}>
            <VStack align="start" spacing={2}>
              {options.map((option, index) => (
                <Radio key={index} value={option}>
                  {option}
                </Radio>
              ))}
            </VStack>
          </RadioGroup>
          {selectedValue && (
            <Button
              size="sm"
              variant="outline"
              colorScheme="purple"
              mt={2}
              onClick={handleClearSelection}
            >
              Clear selection
            </Button>
          )}
        </Box>
      )}

      {isSaving && (
        <FormHelperText color="blue.500" fontSize="xs">
          Saving...
        </FormHelperText>
      )}

      {required && !hasValue && (
        <FormErrorMessage>Please select an option</FormErrorMessage>
      )}
    </FormControl>
  );
}
